import { engineComponent } from "neo-common";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Symbol } from "../GameComponents/Symbol";
import { Settings } from "./Settings";
import { Configuration } from "../Components/Configuration";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Tweens } from "../Components/Effects/Tweens";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { ImageTextElement } from "../Components/Elements/Texts/ImageTextElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { ModuleManager } from "../Managers/ModuleManager";

@engineComponent(ComponentTypes.DisplayObject)
export class ClockSelector extends SwitchElement {
    public static readonly MultiplierFinished: string = "eventMultiplierFinished";
    public static readonly FSFinished: string = "eventFSFinished";
    public static readonly SelectionFinished: string = "eventSelectionFinished";

    private clockTimer: FrameTimerEventEmitter;
    private multiplierTimer: FrameTimerEventEmitter;
    private FSTimer: FrameTimerEventEmitter;
    private startTimer: FrameTimerEventEmitter;
    private soundLoopTimer: FrameTimerEventEmitter;

    private startDelay: number = 0;
    private multiplier: number = 0;
    private freespins: number = 0;
    private popOutro: boolean = false;
    private mX: number = 0;
    private mY: number = 0;
    private mDuration: number = 120;
    private mDelay: number = 0;
    private mEase: string = "linear";

    private numbers: (BaseElement | ImageElement)[] = [];

    private shadow: BaseElement | ImageElement;
    private hand: BaseElement | ImageElement;

    private soundTimer: {
        uid: string;
        value: number;
        lastValue: number;
    };

    private textMul: ImageTextElement;
    private textFS: ImageTextElement;

    private warmupSteps = 24;
    private baseSteps = 24;
    private stopSteps = 36;
    private pauseFactor = 0.5;
    private startExpo = 5;
    private stopExpo = 7;
    private stopVariance = 4;

    private runMultiplier = 2;

    private runDelay = 120;
    private runDuration = 600;
    private afterRunDelay = 120;
    private fsRunDelay = 60;
    private multiplierRunDelay = 120;
    private runEase = "backOut";

    private symbols: Array<Array<Symbol>> = [];
    private symbolContainer: ContainerElement;

    constructor() {
        super();
        this.clockTimer = CreateFrameTimer("enableTimer");
        this.clockTimer.on(
            FrameTimerEvents.Complete,
            function () {
                PlayList.stop("SND_MultiplierLoop", 15);
                if (ModuleManager.freespinsActive()) {
                    PlayList.resetVolume("SND_FSLoop", 30);
                }
                this.emit(ClockSelector.SelectionFinished);
            }.bind(this),
        );
        this.startTimer = CreateFrameTimer("startTimer");
        this.startTimer.on(
            FrameTimerEvents.Complete,
            function () {
                PlayList.setVolume("SND_BaseLoop", 0, 30);
                PlayList.setVolume("SND_FSLoop", 0, 30);
                this.soundLoopTimer.start(120);
                this.animateHand();
            }.bind(this),
        );
        this.soundLoopTimer = CreateFrameTimer("startTimer");
        this.soundLoopTimer.on(
            FrameTimerEvents.Complete,
            function () {
                PlayList.play("SND_MultiplierLoop", 30);
            }.bind(this),
        );
        this.FSTimer = CreateFrameTimer("enableTimer");
        this.FSTimer.on(
            FrameTimerEvents.Complete,
            function () {
                this.emit(ClockSelector.FSFinished);
            }.bind(this),
        );
        this.multiplierTimer = CreateFrameTimer("enableTimer");
        this.multiplierTimer.on(
            FrameTimerEvents.Complete,
            function () {
                this.emit(ClockSelector.MultiplierFinished);
            }.bind(this),
        );
        this.soundTimer = {
            uid: this.uid + "_soundTimer",
            value: 0,
            lastValue: 0,
        };
    }

    createByNode(node: Element) {
        super.createByNode(node);

        if (node.hasAttribute("Configs")) {
            let configs: Array<string> = node.getAttribute("Configs").split(";");
            for (let i: number = 0; i < configs.length; i++) {
                let config: Element = Configuration.Configurations[configs[i]];
                if (config != null) {
                    this.configurate(config);
                }
            }
        }

        for (let k in this.elements.childs) {
            if (k === "Hand") {
                this.hand = this.elements.childs[k];
            }
            if (k === "Shadow") {
                this.shadow = this.elements.childs[k];
            }
            if (k === "TextFS") {
                this.textFS = this.elements.childs[k];
            }
            if (k === "TextMultiplier") {
                this.textMul = this.elements.childs[k];
            }
            if (k === "Numbers" && this.elements.childs[k] instanceof ContainerElement) {
                for (let v = 0; v < 12; v++) {
                    if (this.elements.childs[k].childs["N" + v]) {
                        this.numbers[v] = this.elements.childs[k].childs["N" + v];
                    }
                }
            }
        }

        this.symbolContainer = new ContainerElement();
        this.addChildAt(this.symbolContainer, 0);
    }
    protected configurate(node: Element): void {
        if (node.hasAttribute("X")) {
            this.mX = parseInt(node.getAttribute("X"));
        }
        if (node.hasAttribute("Y")) {
            this.mY = parseInt(node.getAttribute("Y"));
        }
        if (node.hasAttribute("MergeDelay")) {
            this.mDelay = parseInt(node.getAttribute("MergeDelay"));
        }
        if (node.hasAttribute("RunAfterDelay")) {
            this.afterRunDelay = parseInt(node.getAttribute("RunAfterDelay"));
        }
        if (node.hasAttribute("FSRunDelay")) {
            this.fsRunDelay = parseInt(node.getAttribute("FSRunDelay"));
        }
        if (node.hasAttribute("MultiplierRunDelay")) {
            this.multiplierRunDelay = parseInt(node.getAttribute("MultiplierRunDelay"));
        }
        if (node.hasAttribute("MergeDuration")) {
            this.mDuration = parseInt(node.getAttribute("MergeDuration"));
        }
        if (node.hasAttribute("MergeEase")) {
            this.mEase = node.getAttribute("MergeEase");
        }
    }

    public awaitStart(options: ClockSelectorStartOptions) {
        this.startDelay = options.delay || 0;
        this.multiplier = options.multiplier;
        this.freespins = options.freespins;
        this.popOutro = options.popOutro || false;
    }

    public start(options?: ClockSelectorStartOptions) {
        this.goToCase("EMPTY");
        this.visible = true;
        if (options) {
            this.startDelay = options.delay || 0;
            this.multiplier = options.multiplier;
            this.freespins = options.freespins;
            this.popOutro = options.popOutro || false;
        }

        let initialDelay = 0;
        let hasMerge = false;

        this.goToCase("RESET");

        if (options && options.symbols) {
            options.symbols.forEach((reel, reelIndex) => {
                if (!this.symbols[reelIndex]) {
                    this.symbols[reelIndex] = [];
                }
                reel.forEach((symbol, symbolIndex) => {
                    if (symbol.id === Settings.SymbolBonus - 1) {
                        if (!this.symbols[reelIndex][symbolIndex]) {
                            this.symbols[reelIndex][symbolIndex] = symbol.clone();
                            this.symbolContainer.addChild(this.symbols[reelIndex][symbolIndex]);
                        }
                        symbol.visible = false;
                        const aniSym = this.symbols[reelIndex][symbolIndex];
                        aniSym.position.x = symbol.position.x;
                        aniSym.position.y = symbol.position.y;
                        aniSym.visible = true;
                        //aniSym.alpha = 0.5;

                        aniSym.goToCase("NORMAL");
                        aniSym.goToCase("MERGE");
                        hasMerge = true;

                        Tweens.removeTweens(aniSym);
                        Tweens.positionTween(aniSym, {
                            tweenX: [aniSym.position.x, this.mX],
                            tweenY: [aniSym.position.y, this.mY],
                            tweenEase: this.mEase,
                            tweenDelay: this.mDelay,
                            tweenDuration: this.mDuration,
                            callbackType: "onComplete",
                            callbackFunction: function () {
                                this.goToCase("EMPTY", false, 100);
                            }.bind(aniSym),
                        });
                        initialDelay = this.mDuration + this.mDelay;
                    } else if (this.symbols[reelIndex][symbolIndex]) {
                        this.symbols[reelIndex][symbolIndex].visible = false;
                    }
                });
            });
        }

        if (hasMerge) {
            PlayList.play("SND_BonusSelectorWin");
            PlayList.play("SND_BonusMerge");
        }

        if (initialDelay || this.startDelay) {
            this.startTimer.start(this.startDelay);
        } else {
            this.animateHand();
        }
    }

    public awaitOutro(options: ClockSelectorStartOptions) {
        this.visible = true;
        this.multiplier = options.multiplier;
        this.freespins = options.freespins;
        this.popOutro = options.popOutro || false;

        if (this.textMul) {
            this.textMul.setText("x" + this.multiplier);
            this.textMul.visible = false;
        }

        if (this.textFS) {
            this.textFS.setText(this.freespins + "FS");
            this.textFS.visible = false;
        }

        this.hand.rotation = (this.multiplier * Math.PI) / 6;
        this.shadow.rotation = (this.multiplier * Math.PI) / 6;

        this.goToCase("AWAIT_OUTRO");
    }

    private animateHand() {
        let angles: number[] = [];
        let ticks: number[] = [];
        let scales: PIXI.Point[][] = [];
        let durations: number[] = [];
        let dSum = 0;
        let variance = this.stopVariance ? Math.floor(this.stopVariance * Math.random()) : 0;

        for (let i = 0; i < 12; i++) {
            scales[i] = [];
        }

        let i = 0;
        this.soundTimer.lastValue = 0;

        for (let k = 0; k <= this.warmupSteps + this.baseSteps + (this.multiplier % 12) + this.stopSteps; k++) {
            ticks.push(i);
            i++;
            ticks.push(i);
            angles.push(k * 30);
            angles.push(angles[angles.length - 1]);

            for (let i = 0; i < 12; i++) {
                scales[i].push(k % 12 === i ? new PIXI.Point(1.2, 1.2) : new PIXI.Point(1, 1));
                scales[i].push(scales[i][scales[i].length - 1]);
            }

            if (k <= this.warmupSteps) {
                //let v = (this.expo - (this.expo-1) * k / this.warmupSteps);
                let v = Math.pow(this.startExpo, 1 - k / this.warmupSteps);
                durations.push(this.runMultiplier * v);
                durations.push(durations[durations.length - 1] * this.pauseFactor);
            } else if (k <= this.warmupSteps + this.baseSteps + variance) {
                durations.push(this.runMultiplier);
                durations.push(durations[durations.length - 1] * this.pauseFactor);
            } else {
                let v = Math.pow(
                    this.stopExpo,
                    (k - this.warmupSteps - this.baseSteps - variance) /
                        ((this.multiplier % 12) + this.stopSteps - variance),
                );
                durations.push(this.runMultiplier * v);
                durations.push((durations[durations.length - 1] * this.pauseFactor * (v - 1)) / (this.stopExpo - 1));
            }
        }

        durations.forEach((value) => (dSum += value));
        let keyTimes = [0];
        dSum -= durations.shift();

        let tickDurations: number[] = [];

        let prev = 0;
        for (let k = 0; k < durations.length; k++) {
            keyTimes.push(prev + durations[k] / dSum);
            prev += durations[k] / dSum;
            tickDurations.push((durations[k] / dSum) * this.runDuration);
        }
        keyTimes[keyTimes.length - 1] = 1;

        ticks.pop();
        tickDurations.pop();

        if (this.textMul) {
            this.textMul.setText("x" + this.multiplier);
            this.textMul.visible = false;
        }

        if (this.textFS) {
            this.textFS.setText(this.freespins + "FS");
            this.textFS.visible = false;
        }

        Tweens.removeTweens(this.shadow);
        Tweens.rotationTween(this.shadow, {
            tweenDelay: this.runDelay,
            tweenDuration: this.runDuration,
            tweenEase: this.runEase,
            degrees: angles.slice(),
            keyTimes: keyTimes.slice(),
        });

        Tweens.removeTweens(this.hand);
        Tweens.rotationTween(this.hand, {
            tweenDelay: this.runDelay,
            tweenDuration: this.runDuration,
            tweenEase: this.runEase,
            degrees: angles,
            keyTimes: keyTimes.slice(),
            callbackFunction: this.goToCase.bind(this, this.popOutro ? "POP_OUTRO" : "POP"),
            callbackType: "onComplete",
        });

        Tweens.removeTweens(this.soundTimer);
        Tweens.customTween(this.soundTimer, {
            tweenDelay: [this.runDelay],
            /*tweenDuration: [ this.runDuration ],*/
            tweenDuration: tickDurations,
            tween: "value",
            tweenValues: ticks,
            /*keyTimes: keyTimes.slice(),*/
            callbackFunction: function () {
                if (this.value - this.lastValue >= 1) {
                    this.lastValue++;
                    PlayList.play("SND_BaseTick");
                }
            }.bind(this.soundTimer),
            callbackType: "onUpdate",
        });

        this.FSTimer.start(this.runDelay + this.runDuration + this.fsRunDelay);
        this.multiplierTimer.start(this.runDelay + this.runDuration + this.multiplierRunDelay);
        this.clockTimer.start(this.runDelay + this.runDuration + this.afterRunDelay);

        for (let i = 0; i < 12; i++) {
            const obj = this.numbers[i];
            Tweens.removeTweens(obj);
            obj.scale.x = obj.scale.y = 1;
            Tweens.scaleTween(obj, {
                tweenDelay: this.runDelay,
                tweenDuration: this.runDuration,
                tweenEase: "expoOut",
                keyTimes: keyTimes.slice(),
                tweenValues: scales[i],
            });

            if (
                this.elements.childs["Highlights"] instanceof ContainerElement &&
                this.elements.childs["Highlights"].childs["N" + i]
            ) {
                this.elements.childs["Highlights"].childs["N" + i].visible = this.multiplier % 12 === i;
            }
        }

        this.goToCase("SPIN");
    }
}

export interface ClockSelectorStartOptions {
    multiplier: number;
    freespins: number;
    popOutro?: boolean;
    symbols?: Array<Array<Symbol>>;
    delay?: number;
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ClockSelector.ts
 **/